import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { CheckCircle, ArrowRight, ShoppingBag, Phone } from "lucide-react";

const SITE = SITE_URL;

const faqs = [
  {
    q: "Do you rent the Baby Brezza Formula Pro near Disney World?",
    a: "Yes. We rent the Baby Brezza Formula Pro — an automatic formula maker that dispenses a perfectly mixed, warmed bottle at the press of a button. It is available for delivery to Walt Disney World resorts, Universal Orlando, Kissimmee vacation homes and area hotels.",
  },
  {
    q: "Why rent a Baby Brezza instead of bringing mine to Disney?",
    a: "Flying with a Baby Brezza means checked-bag fees, airport hassle and hoping it survives the journey. Renting one from Enchanted Gear Rentals means you land with carry-ons only and find everything clean and ready at your resort. No packing, no unpacking, no damage risk.",
  },
  {
    q: "How does the Baby Brezza Formula Pro work?",
    a: "The Baby Brezza Formula Pro holds a container of powdered formula and a water tank. You press one button and it dispenses a bottle at your chosen temperature and volume — no measuring, no mixing, no guessing. It is compatible with the most popular formula brands.",
  },
  {
    q: "Do you deliver the Baby Brezza to Disney resort rooms?",
    a: "Yes. We deliver to every Walt Disney World resort. Disney does not allow third-party vendors to leave items with bell services, so delivery and pickup require an in-person lobby meeting. We coordinate the handoff time with you directly.",
  },
  {
    q: "Is the Baby Brezza cleaned before delivery?",
    a: "Yes. Every Baby Brezza rental is fully sanitized before delivery. The water tank, formula container and all removable parts are cleaned and dried before each rental.",
  },
  {
    q: "Can I rent the Baby Brezza with a stroller or crib at the same time?",
    a: "Yes. Most families bundle the Baby Brezza with a crib and stroller. Add everything you need to your cart at checkout — all items are delivered together in a single trip with no extra delivery charge.",
  },
  {
    q: "How far in advance should I book the Baby Brezza rental?",
    a: "We recommend booking at least a few days before your trip. Baby Brezza Formula Pro units can sell out during peak Disney season (spring break, summer, holidays). Earlier is always better to guarantee availability.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Baby Gear", item: `${SITE}/gear` },
    { "@type": "ListItem", position: 3, name: "Baby Brezza Rental Orlando", item: `${SITE}/baby-brezza-rental-orlando` },
  ],
};

export default function BabyBrezzaRental() {
  return (
    <>
      <PageSEO
        title="Baby Brezza Formula Pro Rental Near Disney World | Orlando Baby Gear Rental"
        description="Rent a Baby Brezza Formula Pro near Walt Disney World. Skip the checked bags — we deliver a clean, ready-to-use Baby Brezza to your Disney resort. Enchanted Gear Rentals, Orlando baby gear specialists."
        canonical={`${SITE}/baby-brezza-rental-orlando`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/gear" className="hover:text-[#FFD166]">Baby Gear</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Baby Brezza Rental</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Baby Brezza Formula Pro Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              No measuring. No mixing. No 3 am guesswork. Rent a Baby Brezza Formula Pro for your Orlando or Disney World vacation — delivered clean and ready to use to your resort, vacation home or hotel.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="brezza-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/gear"><button className="btn-ghost">Browse All Baby Gear</button></Link>
            </div>
          </div>
        </section>

        {/* What is it */}
        <section className="container py-14">
          <div className="grid md:grid-cols-2 gap-10 items-start max-w-4xl">
            <div>
              <h2 className="font-serif text-3xl text-[#F6F1E4] mb-4">What is the Baby Brezza Formula Pro?</h2>
              <p className="text-[#B9C8D5] text-sm mb-4">
                The Baby Brezza Formula Pro is an automatic formula maker that works like a coffee machine for baby formula. Load your formula powder into the container, fill the water tank, choose your bottle size and temperature, and press one button. The machine dispenses a perfectly mixed, warmed bottle in seconds.
              </p>
              <p className="text-[#B9C8D5] text-sm mb-4">
                It eliminates the guesswork and mess of hand-mixing formula — especially useful at 3 am in a Disney resort room when you are tired and your baby is hungry.
              </p>
              <ul className="space-y-2 text-sm text-[#B9C8D5]">
                {[
                  "Works with most major formula brands",
                  "Dispenses at your chosen temperature — warm or room temp",
                  "No measuring — precise ratio every time",
                  "Holds enough formula for multiple days of bottles",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
              <ShoppingBag className="w-6 h-6 text-[#FFD166] mb-4" />
              <h3 className="font-serif text-xl text-[#F6F1E4] mb-4">Why rent instead of pack?</h3>
              <div className="space-y-3 text-sm text-[#B9C8D5]">
                {[
                  { label: "Skip checked-bag fees", desc: "Baby Brezza Formula Pro is bulky. Checking it as luggage costs $35–50 each way and risks damage." },
                  { label: "Arrives clean and ready", desc: "Ours is fully sanitized before delivery. You do not need to clean it yourself on arrival." },
                  { label: "Bundle with other gear", desc: "Add a crib, stroller or high chair in the same order — single delivery trip, no extra charge." },
                  { label: "Return at pickup", desc: "We collect it when your rental ends. No repacking, no shipping." },
                ].map(({ label, desc }) => (
                  <div key={label}>
                    <span className="font-semibold text-[#F6F1E4]">{label}.</span> {desc}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Delivery info */}
        <section className="container py-10">
          <div className="max-w-2xl rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Baby Brezza delivery near Disney World</h2>
            <p className="text-[#B9C8D5] text-sm mb-4">
              We deliver to every Walt Disney World resort — from Grand Floridian and Polynesian to All-Star Movies and Art of Animation. Disney does not allow third-party vendors to leave items at bell services, so all Disney resort deliveries require an in-person lobby meeting.
            </p>
            <ul className="space-y-2 text-sm text-[#B9C8D5]">
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Free standard delivery to all Disney World resorts</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Also deliver to Universal Orlando, vacation homes and area hotels</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />1-hour delivery windows from 7 am to 10 pm</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Delivery is always free</li>
            </ul>
          </div>
        </section>

        {/* Bundle */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">Bundle Baby Brezza with other gear</h2>
          <p className="text-[#B9C8D5] mb-8 max-w-2xl">Most families renting a Baby Brezza also need a crib and stroller. Add everything in one order — it all arrives together in a single delivery trip with no extra delivery fee.</p>
          <div className="grid sm:grid-cols-3 gap-5 max-w-3xl">
            {[
              { title: "Full-Size Crib", desc: "Fresh linens included. We set it up in your room at delivery.", link: "/crib-rental-disney-world" },
              { title: "Strollers", desc: "Single and double options from Baby Jogger, Zoe, Nuna and more.", link: "/strollers" },
              { title: "High Chair", desc: "Full-size high chair for mealtime at the resort or vacation home.", link: "/gear" },
            ].map((item) => (
              <Link key={item.title} to={item.link} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all">
                <h3 className="font-semibold text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-2">{item.title}</h3>
                <p className="text-sm text-[#B9C8D5] mb-3">{item.desc}</p>
                <span className="text-xs text-[#FFD166] flex items-center gap-1">View options <ArrowRight className="w-3 h-3" /></span>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Baby Brezza rental FAQ — Disney World &amp; Orlando</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Also renting for your Disney trip?</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Crib rental Disney World", to: "/crib-rental-disney-world" },
              { label: "Baby gear rental Disney World", to: "/baby-gear-rental-disney-world" },
              { label: "Strollers near Disney World", to: "/disney-world-stroller-rentals" },
              { label: "Last-minute stroller rental", to: "/last-minute-stroller-rental-disney-world" },
              { label: "Orlando stroller rentals", to: "/stroller-rentals-orlando" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your Baby Brezza for Disney World</h2>
            <p className="text-[#B9C8D5] mb-6">Check availability and secure your Baby Brezza rental in minutes. Questions? Call or text us.</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="brezza-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <a href="tel:+16893457368"><button className="btn-ghost"><Phone className="inline w-4 h-4 mr-2" />689-345-7368</button></a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
